import React from 'react'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import i18n from 'config/i18n'

import { PartiposterProfile, SocialMediaAccount, SubmissionState } from 'state/submissions'
import { parseBackendDate } from 'services/dates'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import { Country, PLATFORM_ENUM } from 'services/api/constants'
import { GridColDefProps } from '../SimpleTable/constants'
import { ProfileCell } from 'components/Shared/MuiTable/Cell/ProfileCell'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'
import { SocialMediaInfoCell } from 'components/Shared/MuiTable/Cell/SocialMediaInfoCell'

export interface RowsProps {
    id: string
    uuid: string
    profile: { profile: PartiposterProfile }
    socialMediaInfo: { profiles: SocialMediaAccount[] }
    completedTime: {
        value: string
        color?: string
    }
}

export const getColumns = (): GridColDefProps[] => {
    return [
        {
            field: 'profile',
            type: 'string',
            headerName: i18n.t('submission.table.profile'),
            width: 160,
            minWidth: 60,
            sortable: false,
            renderCell: (props: GridRenderCellParams) => <ProfileCell {...props} />,
            subFields: [
                {
                    field: 'name',
                    headerName: i18n.t('submission.table.nameLabel'),
                },
                {
                    field: 'gender',
                    headerName: i18n.t('submission.table.genderLabel'),
                },
                {
                    field: 'age',
                    headerName: i18n.t('submission.table.ageLabel'),
                },
            ],
        },
        {
            field: 'socialMediaInfo',
            type: 'string',
            headerName: i18n.t('submission.table.socialMediaInfo'),
            width: 180,
            minWidth: 60,
            sortable: false,
            renderCell: (props: GridRenderCellParams) => <SocialMediaInfoCell {...props} />,
            subFields: [
                {
                    field: FIELDS_TYPE.IG_USERNAME,
                    headerName: i18n.t('submission.table.instagramUsernameLabel'),
                },
                {
                    field: FIELDS_TYPE.FB_USERNAME,
                    headerName: i18n.t('submission.table.facebookUsernameLabel'),
                },
                {
                    field: FIELDS_TYPE.TIKTOK_USERNAME,
                    headerName: i18n.t('submission.table.tikTokUsernameLabel'),
                },
                {
                    field: FIELDS_TYPE.TWITTER_USERNAME,
                    headerName: i18n.t('submission.table.twitterUsernameLabel'),
                },
                {
                    field: FIELDS_TYPE.XIAOHONGSHU_USERNAME,
                    headerName: i18n.t('submission.table.XHSUsernameLabel'),
                },
            ],
        },
        {
            field: 'completedTime',
            type: 'dateTime',
            headerName: i18n.t('submission.table.completedTimeLabel'),
            flex: 1,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            sortable: false,
            filterable: false,
        },
    ]
}

export const getRows = (
    submissions: SubmissionState[],
    profileById: Record<string, PartiposterProfile>,
    country: Country,
): RowsProps[] => {
    return submissions.map(submission => {
        const profile = profileById[submission.partiposterId]
        const { social_media_accounts: socialMediaAccounts } = profile
        const igProfile = socialMediaAccounts.filter(account => account.platform === PLATFORM_ENUM.IG)[0]
        const fbProfile = socialMediaAccounts.filter(account => account.platform === PLATFORM_ENUM.FB)[0]
        const tiktokProfile = socialMediaAccounts.filter(account => account.platform === PLATFORM_ENUM.TIKTOK)[0]
        const twitterProfile = socialMediaAccounts.filter(account => account.platform === PLATFORM_ENUM.TWITTER)[0]
        const xiaohongshuProfile = socialMediaAccounts.filter(
            account => account.platform === PLATFORM_ENUM.XIAOHONGSHU,
        )[0]

        return {
            id: submission.uuid,
            uuid: submission.uuid,
            profile: {
                profile,
            },
            socialMediaInfo: { profiles: [igProfile, fbProfile, tiktokProfile, twitterProfile, xiaohongshuProfile] },
            completedTime: { value: parseBackendDate(submission.submitted_at, country, { showTime: true }) },
        }
    })
}
